<template>
  <v-card>
    <v-container
      v-show="!isPayrollDetail"
      class="pa-0"
    >
      <v-row
        v-if="payrollCycles.length > 0"
        class="px-3"
      >
        <v-col cols="2">
          <payroll-payroll-cycle-dialog-create
            v-if="$hasPermissionTo(['create payroll transactions']) && creatable"
            :payroll-period-id="payrollPeriodId"
            :existing-payroll-cycles="existingPayrollCycles"
            @created="loadPayrollCycle(payrollPeriodId)"
          />
        </v-col>
        <v-col cols="4" />
      </v-row>

      <v-data-table
        :items="payrollCycles"
        :headers="headers"
        :loading="payrollCycleLoading"
        disable-pagination
        hide-default-footer
        class="pb-4"
      >
        <template v-slot:item.payroll_cycle.name="{ item }">
          <div
            class="cursor-pointer"
            @click="item.status == 'PENDING' ? showDetails(true,item) : $emit('created',item)"
          >
            <span>{{ item.payroll_cycle.name }}</span>
          </div>
        </template>

        <template v-slot:item.status="{ item }">
          <span
            v-if="item.status == 'CLOSED'"
            class="text-success"
          >
            {{ item.status }}
          </span>
          <span
            v-else-if="item.status == 'COMPUTED'"
            class="text-primary"
            :class="{
              'red--text font-weight-bold': item.payroll_cycle_employees_count > 0
            }"
          >
            {{ item.status }}
          </span>
          <span
            v-else
            class="text-warning"
          >
            {{ item.status }}
          </span>
        </template>

        <template v-slot:item.created_at="{ item }">
          <div>
            <span>{{ $moment(item.created_at, 'YYYY-MM-DD').format('DD MMM YYYY') }}</span>
          </div>
        </template>

        <template v-slot:item.action="{ item }">
          <div>
            <payroll-payroll-period-dialog-create-company
              v-if="$hasPermissionTo(['create payroll transactions']) && item.status=='PENDING'"
              :payroll-cycle-transaction="item"
            />
            <v-tooltip
              v-if="$hasPermissionTo(['create payroll transactions']) && item.status=='PENDING'"
              bottom
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  color="green"
                  icon
                  @click="generateTransactionsReport(item)"
                  v-on="on"
                >
                  <v-icon>
                    mdi-file-excel
                  </v-icon>
                </v-btn>
              </template>
              <span>Export Transactions Report</span>
            </v-tooltip>
            <payroll-payroll-period-dialog-adjust-part-timer-salary
              v-if="$hasPermissionTo(['create payroll transactions'])"
              :payroll-period="payrollPeriod"
              :payroll-cycle-transaction="item"
              @created="loadPayrollCycle(payrollPeriodId)"
            />
            <payroll-payroll-cycle-computation-dialog-create
              v-if="item.status=='PENDING'"
              :payroll-cycle-transaction="item"
              @created="loadPayrollCycle(payrollPeriodId)"
              @payrollComputed="$emit('payrollComputed')"
            />
            <payroll-payroll-period-dialog-revert
              v-if="$hasPermissionTo(['create payroll transactions']) && item.status=='COMPUTED'"
              :payroll-cycle-transaction="item"
              @reverted="loadPayrollCycle(payrollPeriodId)"
              @payrollReverted="$emit('payrollReverted')"
            />
            <payroll-payroll-period-dialog-sync-unpaid-leave
              v-if="$hasPermissionTo(['create payroll transactions']) && item.status != 'CLOSED'"
              :payroll-period="payrollPeriod"
              :payroll-cycle-transaction="item"
              @synced="loadPayrollCycle(payrollPeriodId)"
            />
            <payroll-payroll-cycle-dialog-release-payslip
              v-if="$hasPermissionTo(['create payroll transactions']) && item.status!='PENDING'"
              :payroll-period="payrollPeriod"
              :payroll-cycle-transaction="item"
            />            
            <payroll-payroll-period-dialog-add-doe
              v-if="$hasPermissionTo(['create payroll transactions']) && item.status == 'COMPUTED'"
              :payroll-period="payrollPeriod"
              :payroll-cycle-transaction="item"
              @created="loadPayrollCycle(payrollPeriodId)"
            />
            <payroll-payroll-period-dialog-add-company-employee
              v-if="$hasPermissionTo(['create payroll transactions']) && item.status == 'COMPUTED'"
              :payroll-period="payrollPeriod"
              :payroll-cycle-transaction="item"
              @created="loadPayrollCycle(payrollPeriodId)"
            />
            <compute-for-missing-statutories-confirmation
              v-if="$hasPermissionTo(['create payroll transactions']) && item.status == 'COMPUTED' && (missingCompanyEmployeesStatutories[item.id] || []).length > 0"
              :missing-company-employees-statutories="missingCompanyEmployeesStatutories[item.id]"
              :payroll-cycle-transaction="item"
              @computed="loadPayrollCycle(payrollPeriodId)"
            />
          </div>
        </template>
      </v-data-table>
    </v-container>

    <payroll-payroll-cycle-transaction-detail-container-view
      v-show="isPayrollDetail && $hasPermissionTo(['create payroll transactions'])"
      ref="transactionDetail"
      :payroll-cycle-transaction="payrollCycleTransaction"
    >
      <template v-slot:navigation>
        <v-breadcrumbs 
          :items="navItems" 
          divider="/"
          class="py-0"
          large
        >
          <template v-slot:item="{ item }">
            <v-breadcrumbs-item
              :disabled="item.disabled"
              @click="showDetails(false,item)"
            >
              <span :class="{'cursor-pointer color-primary': !item.disabled}">{{ item.text }} </span>
            </v-breadcrumbs-item>
          </template>
        </v-breadcrumbs>
      </template>
    </payroll-payroll-cycle-transaction-detail-container-view>
  </v-card>
</template>

<script>
  export default {
    components: {
      PayrollPayrollCycleDialogCreate: () => import('@/components/hris/payroll/payroll-cycle/DialogCreate'),
      PayrollPayrollCycleTransactionDetailContainerView: () => import('@/components/hris/payroll/payroll-cycle/transaction-detail/ContainerView'),
      PayrollPayrollPeriodDialogCreateCompany: () => import('@/components/hris/payroll/payroll-period/DialogCreateCompany'),
      PayrollPayrollCycleComputationDialogCreate: () => import('@/components/hris/payroll/payroll-cycle/computation/DialogCreate'),
      PayrollPayrollPeriodDialogRevert: () => import('@/components/hris/payroll/payroll-period/DialogRevert'),
      PayrollPayrollPeriodDialogSyncUnpaidLeave: () => import('@/components/hris/payroll/payroll-period/DialogSyncUnpaidLeave'),
      PayrollPayrollCycleDialogReleasePayslip: () => import('@/components/hris/payroll/payroll-cycle/DialogReleasePayslip'),
      PayrollPayrollPeriodDialogAddDoe: () => import('@/components/hris/payroll/payroll-period/DialogAddDoe'),
      PayrollPayrollPeriodDialogAddCompanyEmployee: () => import('@/components/hris/payroll/payroll-period/DialogAddCompanyEmployee'),
      PayrollPayrollPeriodDialogAdjustPartTimerSalary: () => import('@/components/hris/payroll/payroll-period/DialogAdjustPartTimerSalary'),
      ComputeForMissingStatutoriesConfirmation: () => import('@/components/hris/payroll/payroll-period/DialogComputeForMissingPayrollStatutories'),
    },
    props: {
      payrollPeriod: {
        type: Object,
        default: null,
      },
    },
    data () {
      return {
        payrollCycles: [],
        headers: [
          {
            text: 'Payroll Cycle',
            sortable: false,
            value: 'payroll_cycle.name',
            align: 'left',
          },
          {
            text: 'Status',
            sortable: false,
            value: 'status',
            align: 'center',
          },
          {
            text: 'Closed On',
            sortable: false,
            value: 'closed_on',
            align: 'center',
          },
          {
            text: 'Transaction Open Date',
            sortable: false,
            value: 'created_at',
            align: 'center',
          },
          {
            text: '',
            sortable: false,
            value: 'action',
            align: 'left',
          },
        ],
        payrollCycleLoading: false,
        missingCompanyEmployeesStatutories: {},
        payrollPeriodId: null,
        existingPayrollCycles: [],
        isPayrollDetail: false,
        navItems: [
          {
            text: 'Overview',
            disabled: false,
          },
          {
            text: 'Payroll Detail',
            disabled: true,
          },
        ],
        payrollCycleTransaction: null,
      }
    },

    computed: {
      creatable () {
        let isPending = true

        this.payrollCycles.map(payrollCycle => {
          if (payrollCycle.status === 'PENDING') {
            isPending = false
          }
        })
        return isPending
      },
    },

    methods: {
      generateTransactionsReport (item) {
        // this.progressing = true
        this.$api.payrollCycleTransactions().report(this.payrollPeriodId, this.prepareFormData(item))
          .then(res => {
            let blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
            let link = document.createElement('a')
            link.href = window.URL.createObjectURL(blob)
            link.setAttribute('download', 'TransactionReport.xlsx') 
            link.click()

            // this.progressing = false
          })
      },
      prepareFormData (item) {
        return {
          payroll_cycle_transaction_id: item.id,
        }
      },
      loadPayrollCycle (id) {
        this.payrollCycles = []
        this.payrollCycleLoading = true

        this.payrollPeriodId = id

        this.$api.payrollPeriodCycles().list(id)
          .then(res => {
            this.payrollCycles = _.orderBy(res.data, ['created_at', 'payroll_cycle.name'], ['asc', 'asc'])

            this.existingPayrollCycles = this.payrollCycles.map(item => item.payroll_cycle.id)
            this.payrollCycleLoading = false

            // refresh every computed cycle, since computing any cycle can create statutories for employees in other cycles
            this.missingCompanyEmployeesStatutories = {}
            this.payrollCycles
              .filter(item => item.status === 'COMPUTED')
              .forEach(item => this.missingStatutories(item))
          })
      },

      missingStatutories (payrollCycleTransaction) {
        this.$api.hrisCompanyEmployees().list('?payroll_cycle_transaction_id=' + payrollCycleTransaction.id)
          .then(res => {
            this.$set(this.missingCompanyEmployeesStatutories, payrollCycleTransaction.id, res.data)
          }).catch((e) => {
            this.$snack({ color: 'error', message: 'Error! Unable to load missing company employees.' })
          })
      },
      clearPayrollCycles () {
        this.payrollCycles = []
      },

      showDetails (isShowDetail, payrollCycleTransaction) {
        this.payrollCycleTransaction = payrollCycleTransaction
        if (isShowDetail) {
          this.$refs.transactionDetail.loadPayrollCycleEmployees(payrollCycleTransaction.id)
        }

        this.isPayrollDetail = isShowDetail
      },
    },
  }
</script>

<style scoped>
.cursor-pointer{
  cursor: pointer;
} 
.color-primary{
  color:#9c27B0;
}
</style>
